import GeoIpClient, {
  ErrorResp,
  UNHANDLED_ERROR_CODE,
  UNHANDLED_ERROR_MESSAGE,
} from '../geoip-client';
import {
  getCachedResponse,
  setCachedResponse,
} from '../geoip-client/demo-cache';
import { GeoIpDemoResponse } from '../geoip-client/types';
import {
  IP_ADDRESS_NOT_FOUND_CODE,
  notFoundMessageNode,
} from './ip-address-not-found';

interface GeoIpDemoProps {
  demoResultTableBody: HTMLTableSectionElement | null;
  lookupForm: HTMLFormElement | null;
  noIpAddressRow: HTMLTableRowElement | null;
  anycastText: HTMLParagraphElement | null;
}

class GeoIpDemo {
  #anycastText: GeoIpDemoProps['anycastText'];
  #demoResultTableBody: GeoIpDemoProps['demoResultTableBody'];
  #ipDemoBaseUrl: string | undefined;
  #lookupForm: GeoIpDemoProps['lookupForm'];
  #noIpAddressRow: GeoIpDemoProps['noIpAddressRow'];
  #geoipClient: GeoIpClient;

  constructor(props: GeoIpDemoProps) {
    this.#anycastText = props.anycastText;
    this.#demoResultTableBody = props.demoResultTableBody;
    this.#ipDemoBaseUrl = document.querySelector<HTMLTableElement>(
      '.geoip-demo-form__table',
    )?.dataset.ipDemoBaseUrl;
    this.#lookupForm = props.lookupForm;
    this.#noIpAddressRow = props.noIpAddressRow;

    if (this.#lookupForm === null) {
      throw new Error('Unable to find lookup form');
    }

    const csrfToken = new FormData(this.#lookupForm).get('csrf_token');

    if (csrfToken === null) {
      throw new Error('Unable to find CSRF token');
    }

    this.#geoipClient = new GeoIpClient({
      csrfToken: csrfToken as string,
      isDemo: true,
    });

    this.#lookupForm.addEventListener('submit', async (event) => {
      await this.handleLookupSubmit(event);
    });

    this.handleQueryParam();
  }

  private async handleLookupSubmit(e: Event) {
    e.preventDefault();

    // Clear previous rows
    if (this.#demoResultTableBody) {
      this.#demoResultTableBody.innerHTML = '';
    }

    const formData = new FormData(e.target as HTMLFormElement);
    const ipAddresses = formData.get('ip_addresses') ?? '';

    const ipAddressesArray = this.toArray(ipAddresses);
    const resultFragment = new DocumentFragment();
    const dailyLimit = 25;
    const ipsToLookup = ipAddressesArray.slice(0, dailyLimit);

    // Separate cached and uncached IPs
    const cachedResults = new Map<string, GeoIpDemoResponse>();
    const uncachedIps: string[] = [];

    for (const ip of ipsToLookup) {
      const cached = getCachedResponse(ip, 'city');
      if (cached) {
        cachedResults.set(ip, cached.data as GeoIpDemoResponse);
      } else {
        uncachedIps.push(ip);
      }
    }

    try {
      // Only call API for uncached IPs
      let apiResults: (GeoIpDemoResponse | ErrorResp)[] = [];
      if (uncachedIps.length > 0) {
        apiResults =
          await this.#geoipClient.lookupIpAddresses<GeoIpDemoResponse>(
            uncachedIps,
            'city',
          );
      }

      // Merge results in original order
      for (const ip of ipsToLookup) {
        const cached = cachedResults.get(ip);
        if (cached) {
          const resultRow = this.createResultRow(ip, cached);
          resultFragment.appendChild(resultRow);
        } else {
          const result = apiResults.shift();
          if (!result) {
            continue;
          }
          if (!('error' in result)) {
            setCachedResponse(result.traits?.ip_address ?? ip, result, 'city');
          }
          const resultRow = this.createResultRow(ip, result);
          resultFragment.appendChild(resultRow);
        }
      }

      const overLimitIPs = ipAddressesArray.slice(
        dailyLimit,
        ipAddressesArray.length,
      );

      overLimitIPs.forEach((ip) => {
        const errorResultRow = this.createErrorRow(
          ip,
          'You have used all 25 of your demo queries for the day.',
        );
        resultFragment.appendChild(errorResultRow);
      });
    } catch {
      resultFragment.appendChild(
        this.createErrorRow('', 'There was an issue with this request.'),
      );
    }
    this.#demoResultTableBody?.appendChild(resultFragment);
  }

  private async handleQueryParam() {
    if (
      window.location.pathname.includes('geoip-demo') ||
      window.location.pathname.includes('geoip-web-services-demo')
    ) {
      const urlParams = new URLSearchParams(window.location.search);

      if (urlParams.has('ip_address')) {
        const ipAddress = urlParams.get('ip_address') ?? '';
        const textarea = document.querySelector<HTMLTextAreaElement>(
          '.js-geoip-demo-textarea',
        );

        if (textarea) {
          textarea.value = ipAddress;
        }
        this.#noIpAddressRow?.classList.add('d-none');

        const cached = getCachedResponse(ipAddress, 'city');
        if (cached) {
          const resultRow = this.createResultRow(
            ipAddress,
            cached.data as GeoIpDemoResponse,
          );
          this.#demoResultTableBody?.appendChild(resultRow);
          return;
        }

        try {
          const result =
            await this.#geoipClient.lookupIpAddresses<GeoIpDemoResponse>(
              [ipAddress],
              'city',
            );
          if (result[0] && !('error' in result[0])) {
            setCachedResponse(
              result[0].traits?.ip_address ?? ipAddress,
              result[0],
              'city',
            );
          }
          const resultRow = this.createResultRow(ipAddress, result[0]);
          this.#demoResultTableBody?.appendChild(resultRow);
        } catch {
          this.#demoResultTableBody?.appendChild(
            this.createErrorRow('', 'There was an issue with this request'),
          );
        }
      }
    }
  }

  private toArray(input: FormDataEntryValue) {
    const array = input
      .toString()
      .trim()
      .split(/[ ,\n]+/);
    return [...new Set(array)];
  }

  private createResultRow(
    ipAddress: string,
    result: GeoIpDemoResponse | ErrorResp,
  ): HTMLTableRowElement {
    if ('error' in result) {
      let message: string | Node = result.error;
      if (result.code === IP_ADDRESS_NOT_FOUND_CODE) {
        message = notFoundMessageNode();
      } else if (result.code === UNHANDLED_ERROR_CODE) {
        message = UNHANDLED_ERROR_MESSAGE;
      }
      return this.createErrorRow(ipAddress, message);
    }

    return this.createValidIpRow(result);
  }

  private createErrorRow(
    ipAddress: string,
    message: string | Node,
  ): HTMLTableRowElement {
    const tr = document.createElement('tr');
    const ipAddressTd = document.createElement('td');
    const errorTd = document.createElement('td');
    const warningIcon = document.createElement('i');

    warningIcon.className =
      'bi bi-exclamation-triangle-fill geoip-demo-form__warning-icon';

    ipAddressTd.textContent = ipAddress;
    ipAddressTd.className = 'geoip-demo-form__ip-address';

    errorTd.setAttribute('colspan', '7');
    errorTd.append(warningIcon, message);

    tr.append(ipAddressTd, errorTd);
    return tr;
  }

  private showAnycastText(result: GeoIpDemoResponse) {
    if (!this.#anycastText?.classList.contains('d-none')) {
      this.#anycastText?.classList.add('d-none');
    }

    if (result.traits?.is_anycast === true) {
      this.#anycastText?.classList.remove('d-none');
    }
  }

  private createValidIpRow(result: GeoIpDemoResponse): HTMLTableRowElement {
    const city = result.city?.names?.en ? `${result.city.names.en},` : '';
    const subdivision = result.subdivisions?.[0]?.names?.en
      ? `${result.subdivisions[0].names.en},`
      : '';
    const country = result.country?.names?.en ?? '';
    const countryCode = result.country?.iso_code
      ? `(${result.country?.iso_code}),`
      : '';
    const continent = result.continent?.names?.en ?? '';
    const latitude = result.location?.latitude
      ? `${result.location.latitude},`
      : '-';
    const longitude = result.location?.longitude.toString() ?? '';
    const accuracyRadius = result.location?.accuracy_radius
      ? `(${result.location.accuracy_radius} km)`
      : '(-)';
    const isp = result.traits?.isp ?? '-';
    const organization = result.traits?.organization ?? '-';
    const ispOrganization =
      isp === organization ? isp : `${isp}, ${organization}`;

    const ipAddress = result.traits?.ip_address ?? 'Unknown IP address';
    const ipTd = document.createElement('td');
    ipTd.className = 'geoip-demo-form__ip-address';

    if (this.#ipDemoBaseUrl) {
      const link = document.createElement('a');
      link.href = `${this.#ipDemoBaseUrl}/${ipAddress}`;
      link.textContent = ipAddress;
      const iconSpan = document.createElement('span');
      const icon = document.createElement('i');
      icon.className = 'bi bi-box-arrow-up-right';
      iconSpan.append(' ', icon);
      ipTd.append(link, iconSpan);
    } else {
      ipTd.textContent = ipAddress;
    }

    const tableCells = [
      [
        city || subdivision || country || countryCode || continent
          ? `${city} ${subdivision} ${country} ${countryCode} ${continent}`
          : '-',
      ],
      [result.traits?.network ?? '-'],
      [result.postal?.code ?? '-'],
      [
        `${latitude} ${longitude} ${accuracyRadius}`,
        'geoip-demo-form__coordinates',
      ],
      [ispOrganization],
      [result.traits?.domain ?? '-'],
      [result.traits?.connection_type ?? '-'],
    ];

    const tr = document.createElement('tr');
    tr.appendChild(ipTd);

    for (const [cell, className] of tableCells) {
      const td = document.createElement('td');
      td.textContent = cell;
      if (className) {
        td.className = className;
      }
      tr.appendChild(td);
    }

    this.showAnycastText(result);

    return tr;
  }
}

new GeoIpDemo({
  anycastText: document.querySelector<HTMLParagraphElement>(
    '.js-anycast-network-text',
  ),
  demoResultTableBody: document.querySelector<HTMLTableSectionElement>(
    '.js-result-table-tbody',
  ),
  lookupForm: document.querySelector<HTMLFormElement>('.js-geoip-lookup'),
  noIpAddressRow: document.querySelector<HTMLTableRowElement>(
    '.js-no-ip-address-row',
  ),
});
